import './style.scss';
import { DomesticRealEstateCard, OverseasRealEstateCard } from 'views/pages/symbol/module/card';
import { LeaseFundCard } from 'views/pages/symbol/module/card';
import { PagePath, PagePathFactory } from 'constants/pagePath';
import { Description, labelColor, labelSize } from 'views/mutual/description';
import {
  useAccountStatus,
  useDomesticRealEstateItExplanationProgramList,
  useOverseasRealEstateProgramList,
  useProgramList,
} from './hook';
import { LoadingContainer, LoadingSize } from 'views/mutual/loading';
import { SymbolDetailInfo, GetSymbolListQueryVariables } from 'views/utils/graphql/graphql';
import { ComponentControl } from 'views/mutual/componentControl';
import { ProductType, ProductTypeMap } from 'constants/productType';
import { ReactNode, useState } from 'react';
import { useCustomLocationName } from 'views/mutual/location/hooks';
import { AccountType } from 'views/utils/enums';
import { Tabs } from 'views/mutual/tabs';

// ProductTypeを詳細から戻ってきた場合に保持する
export type ProgramListLocationState = {
  productType: ProductType;
};

export const ProgramList = () => {
  const { accountInfo } = useAccountStatus();
  const accountType = accountInfo.data?.GetAccountInfo?.AccountType;
  const [selectedTabIndex, setSelectedIndex] = useState(0);

  const tabItems = (() => {
    switch (accountType) {
      case AccountType.Personal:
        return [
          {
            title: ProductTypeMap[ProductType.DomesticRealEstate].name,
            content: <DomesticRealEstateItExplanationProgramList />,
          },
          {
            title: ProductTypeMap[ProductType.LeaseFund].name,
            content: <CorporateOnlyMessage />,
          },
          {
            title: ProductTypeMap[ProductType.OverseasRealEstate].name,
            content: <CorporateOnlyMessage />,
          },
        ];
      case AccountType.Corporate:
      default:
        return [
          {
            title: ProductTypeMap[ProductType.LeaseFund].name,
            content: <LeaseFundProgramList />,
          },
          {
            title: ProductTypeMap[ProductType.DomesticRealEstate].name,
            content: <DomesticRealEstateItExplanationProgramList />,
          },
          {
            title: ProductTypeMap[ProductType.OverseasRealEstate].name,
            content: <OverseasRealEstateProgramList />,
          },
        ];
    }
  })();

  return (
    <div className="programList">
      <Tabs
        selectedIndex={selectedTabIndex}
        onSelect={setSelectedIndex}
        tabLists={tabItems.map(({ title }) => ({ title }))}
        tabContents={tabItems.map(({ content }) => content)}
      />
    </div>
  );
};

export const LeaseFundProgramList = () => {
  const { symbolListInfo } = useProgramList();
  useCustomLocationName('案件一覧');

  return (
    <LoadingContainer isLoading={symbolListInfo.fetching} size={LoadingSize.Medium}>
      <div className="leaseFundProgramList">
        <div className="programList">
          <ComponentControl
            componentName={LeaseFundList}
            noDataMessege="現在販売中の案件はございません"
            isLoading={symbolListInfo.fetching}
            items={symbolListInfo.data?.GetSymbolList?.Data || []}
          />
        </div>
        <div className="description">
          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※1　賃借人のほかに転借人、保証人等の実質的な与信先が存在する場合は、当該与信先の名称を記載しています。また、賃借人等の名称は正式名称ではなく略称を記載しています。事業の仕組みの詳細および賃借人等の正式名称については、案件資料をご確認ください。"
          />
          <Description
            labelColor={labelColor.gray}
            labelSize={labelSize.small}
            label="※2　購入選択権設定日の経過期間は、本日～購入選択権設定日までの期間を表し、販売資料に表示する経過時期とは異なります。"
          />
        </div>
      </div>
    </LoadingContainer>
  );
};

export const DomesticRealEstateItExplanationProgramList = () => {
  const { symbolListInfo } = useDomesticRealEstateItExplanationProgramList();
  useCustomLocationName('案件一覧', '※国内不動産を主たる信託財産とする信託の受益権が販売対象です');

  return (
    <LoadingContainer isLoading={symbolListInfo.fetching} size={LoadingSize.Medium}>
      <div className="domesticRealEstateItExplanationProgramList">
        <div className="programList">
          {/* {symbolListInfo.data?.GetSymbolList?.Data?.map((d) => {
          return <DomesticRealEstateItExplanationCard to={PagePathFactory.domesticRealEstateItExplanationSymbolDetail(d!.Symbol)} {...d}/>;
          })} */}
          <ComponentControl
            componentName={DomesticRealEstateItExplanationList}
            noDataMessege="現在オンラインによるご契約手続きは一時停止をしております。購入をご希望のお客様は、対面手続きでのご契約を承っておりますので、最寄りの営業店へご連絡いただきますようお願いいたします。オンラインによる契約再開は3月を予定しております。"
            isLoading={symbolListInfo.fetching}
            items={symbolListInfo.data?.GetDomesticRealEstateSymbolList?.Data || []}
          />
        </div>
      </div>
    </LoadingContainer>
  );
};

export const OverseasRealEstateProgramList = () => {
  const { symbolListInfo } = useOverseasRealEstateProgramList();
  useCustomLocationName('案件一覧');

  return (
    <LoadingContainer isLoading={symbolListInfo.fetching} size={LoadingSize.Medium}>
      <div className="overseasRealEstateProgramList">
        <div className="programList">
          <ComponentControl
            componentName={OverseasRealEstateList}
            noDataMessege="現在販売中の案件はございません"
            isLoading={symbolListInfo.fetching}
            items={symbolListInfo.data?.GetOverseasRealEstateSymbolList?.Data || []}
          />
        </div>
      </div>
    </LoadingContainer>
  );
};

export const CorporateOnlyMessage = () => {
  useCustomLocationName('案件一覧');

  return (
    <div className="corporateOnlyMessage">
      <Description
        labelSize={labelSize.large}
        label={'こちらは法人様向け商品です。お申込みの際は、法人アカウントでログインしてください。'}
      />
    </div>
  );
};

export type RequestHistoryListProps = {
  isLoading: boolean;
  items: Array<SymbolDetailInfo | any | null>;
};
export const LeaseFundList = ({ items }: RequestHistoryListProps) => {
  return items?.map((d) => {
    return <LeaseFundCard to={PagePathFactory.leaseFundSymbolDetail(d!.Symbol)} {...d!} />;
  });
};
export const DomesticRealEstateItExplanationList = ({ items }: RequestHistoryListProps) => {
  return items?.map((d) => {
    return (
      <DomesticRealEstateCard to={PagePathFactory.domesticRealEstateItExplanationSymbolDetail(d!.Symbol)} {...d} />
    );
  });
};
export const OverseasRealEstateList = ({ items }: RequestHistoryListProps) => {
  return items?.map((d) => {
    return <OverseasRealEstateCard to={PagePathFactory.overseasRealEstateSymbolDetail(d!.Symbol)} {...d} />;
  });
};
